import React from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Feather } from "@expo/vector-icons";
import { useFocusEffect, useIsFocused } from "@react-navigation/native";
import { capitalize, sortBy } from "lodash";
import { PropsWithChildren, useCallback, useState } from "react";
import { Pressable, TouchableOpacity, View } from "react-native";
import { ActivityIndicator, Text } from "react-native-paper";

import type { Address, Employment, Person, PersonPhone } from "../types";
import type { ScreenProps } from "./types";

import { colors } from "../../../theme";
import { Button, LinkButton, Modal, ScreenContainer, ScrollableContainer } from "../../components";
import DisplayAddress from "../../components/Address";
import InactiveStatusModals from "../../components/InactiveStatusModals";
import PersistentSnackbar from "../../components/PersistentSnackbar";
import ConfirmCancelChangeRequestModal from "../../components/UnsavedChangesModal";
import config from "../../config";
import { useIntercom } from "../../hooks";
import useSnackBar from "../../hooks/useSnackBar";
import ChangeStatusModal from "../EmploymentEditScreen/ChangeStatusModal";
import { ChangeStatusEnum } from "../EmploymentEditScreen/types";
import { ContactChangedNotice } from "../EmploymentRemoveScreen";
import type { CurrentMember } from "../fragments";
import { ADDRESS_FRAGMENT, CURRENT_MEMBER_FRAGMENT, CURRENT_PERSON_FRAGMENT, EMPLOYMENTS_FRAGMENT } from "../fragments";
import EmploymentSection from "./EmploymentSection";
import ForceHomeAddressModal from "./ForceHomeAddressModal";
import PendingChangeRequestsModal from "./PendingChangesModal";
import SentForReviewScreen from "./SentForReviewModal";
import { EmailChangeNotice } from "./SettingsScreen";
import { Section } from "../HomeScreen/types";
import { renderName } from "./utils";

export const ACCOUNT_EDIT_QUERY = gql`
  query currentMemberAccountEditQuery {
    currentMember {
      ...CurrentMemberFragment
    }
    currentPerson {
      ...CurrentPersonFragment
      ...EmploymentsFragment
      primarySectionAffiliation {
        id
        section {
          id
          name
        }
      }
      primarySection {
        id
        name
      }
      latestProgram {
        id
        status
        programType {
          id
          name
        }
        band {
          id
          classification {
            id
            name
            description
          }
        }
      }
    }
  }
  ${CURRENT_MEMBER_FRAGMENT}
  ${CURRENT_PERSON_FRAGMENT}
  ${ADDRESS_FRAGMENT}
  ${EMPLOYMENTS_FRAGMENT}
`;

export const MUTATION = gql`
  mutation updatePersonContactAddress($id: ID!) {
    updatePersonContactAddress(id: $id) {
      message
      success
      person {
        ...CurrentPersonFragment
      }
    }
  }
  ${CURRENT_PERSON_FRAGMENT}
`;

export const CHANGE_STATUS_MUTATION = gql`
  mutation changeMemberStatusRequest($newStatus: MembershipChangeStatusEnum!) {
    changeMemberStatusRequest(newStatus: $newStatus) {
      success
      message
    }
  }
`;

export const CANCEL_PENDING_CHANGE_REQUEST = gql`
  mutation cancelChangeRequest($id: ID!) {
    cancelChangeRequest(id: $id) {
      message
      success
    }
  }
`;

const Divider = () => <View className="border-t border-sky-lighter" />;

const ContactPreferenceDisplay = ({ contactAddress }: { contactAddress?: Address }) => {
  if (!contactAddress) {
    return <Text className="text-regular-tight font-400 text-ink-darker mt-4">None</Text>;
  }

  if (contactAddress.addressType === ("secondary" as "home" | "secondary")) {
    return (
      <View className="flex-row items-center">
        <Feather name="alert-triangle" size={18} color={colors.red.dark} />
        <Text className="text-regular-normal text-red-dark font-400 ml-1">Update your contact preference</Text>
      </View>
    );
  }

  return (
    <>
      <Text className="text-regular-tight font-400 text-ink-darker mb-1" testID="contact-preferences-address">
        {contactAddress.addressable.__typename === "CRMFacility"
          ? contactAddress.addressable.name
          : `${capitalize(contactAddress.addressType)} Address`}
      </Text>
      <DisplayAddress address={contactAddress} />
    </>
  );
};

const isNonActiveClassification = (name?: string) => {
  return [
    "RSV", // reserve
    "LM", // Life Member (Retired)
    "LMA", // life member-active
    "LMC", // life member-century
    "RM", // retired
    "HM", // Honorary Member
    "LMM", // Life Member Master (Retired)
    "LMMA", // Life Member Master - Active
  ].includes(name || "");
};

const NonActiveClassificationStatus = ({
  classification,
  onPress,
  primarySection,
}: {
  classification?: Classification;
  onPress: () => void;
  primarySection?: Section;
}) => (
  <View className="p-6 rounded-lg border border-sky mt-4">
    <View className="flex-row justify-between items-center">
      <View>
        <Text className="mb-1 text-small-tight text-ink-dark">Current Status</Text>
        <Text className="font-700 text-ink-darkest text-regular-tight">{classification?.description}</Text>
      </View>

      <TouchableOpacity testID="change-status-button" onPress={onPress} activeOpacity={0.6}>
        <View className="items-center p-1 rounded justify-center bg-sky-lighter">
          <Feather name="more-horizontal" size={24} color={colors.ink.darkest} />
        </View>
      </TouchableOpacity>
    </View>
    {primarySection ? (
      <>
        <Text className="mb-1 text-small-tight text-ink-dark mt-4">Section</Text>
        <Text className="font-400 text-ink-darkest text-regular-tight">{primarySection?.name}</Text>
      </>
    ) : null}
  </View>
);

const Unemployed = ({
  primarySection,
  onChangeStatus,
  latestProgram,
}: {
  primarySection?: Section;
  onChangeStatus: () => void;
  latestProgram?: Program;
}) =>
  isNonActiveClassification(latestProgram?.band?.classification?.name) ? (
    <NonActiveClassificationStatus
      classification={latestProgram?.band?.classification}
      onPress={onChangeStatus}
      primarySection={primarySection}
    />
  ) : (
    <View className="p-6 rounded-lg border border-sky mt-4">
      <Text className="mb-1 text-small-tight text-ink-dark">Current Status</Text>
      <Text className="font-700 text-ink-darkest text-regular-tight">Unemployed</Text>
      {primarySection ? (
        <View className="mt-5">
          <Text className="mb-1 text-small-tight text-ink-dark">Section</Text>
          <Text className="font-400 text-ink-darkest text-regular-tight">{primarySection?.name}</Text>
        </View>
      ) : null}
      <View className="mt-5">
        <Button mode="contained" onPress={onChangeStatus}>
          Change Status
        </Button>
      </View>
    </View>
  );

export const navigationTitle = "My PGA Account";

type Classification = {
  id: string;
  name: string;
  description: string;
};

enum Status {
  ACTIVE = "ACTIVE",
  SUSPENDED = "SUSPENDED",
  TERMINATED = "TERMINATED",
}

enum ProgramType {
  Member = "Member",
  Associate = "Associate",
  Affiliate = "Affiliate",
  Prospect = "Prospect",
  Student = "Student",
}

type Program = {
  id: string;
  status: Status;
  programType: {
    id: string;
    name: ProgramType;
  };
  band?: {
    id: string;
    classification?: Classification;
  };
};

interface PersonWithProgram extends Person {
  latestProgram?: Program;
}

export type Query = {
  currentMember: CurrentMember;
  currentPerson: PersonWithProgram;
};

const PersonPhone = ({ phone, label }: { phone?: PersonPhone; label: string }) => (
  <>
    <Text className="text-small-tight font-400 text-ink-darker">{label}</Text>
    {phone?.phoneFormatted ? (
      <Text className="text-regular-tight font-400 text-ink-darker mt-1">{phone.phoneFormatted}</Text>
    ) : (
      <Text className="text-regular-tight font-400 text-ink-darker mt-1">N/A</Text>
    )}
  </>
);

const TouchableAddress = ({
  onPress,
  address,
  children,
  isPreferredAddress,
}: PropsWithChildren<{
  onPress(address: Address): void;
  address: Address;
  isPreferredAddress: boolean;
}>) => {
  return (
    <TouchableOpacity activeOpacity={0.7} onPress={() => onPress(address)} testID={`address-touchable-${address.id}`}>
      <View className="px-6 py-4 flex-row">
        <View>
          {children}
          <DisplayAddress address={address} textClassName="text-small-normal" />
        </View>

        <View className="flex-1 flex-row justify-end items-center">
          {isPreferredAddress && <Feather name="check" size={24} color={colors.ink.darkest} testID="check-icon" />}
        </View>
      </View>
      <View className="border-t border-sky-light" />
    </TouchableOpacity>
  );
};

const EditContactPreferencesModal = ({
  visible,
  onDismiss,
  addresses,
  employments,
  onPress,
  contactAddress,
}: {
  visible: boolean;
  onDismiss(): void;
  addresses: Address[];
  employments: Employment[];
  onPress(address: Address): void;
  contactAddress?: Address;
}) => {
  const isPreferredAddress = (address: Address) => address.id === contactAddress?.id;

  const sortOrder = {
    home: 1,
    secondary: 2,
    main: 3,
    mailing: 4,
  };

  const homeAddress = addresses.find((address) => address.addressType === "home");
  const sortAddresses = (addresses: Address[]) => sortBy(addresses, (address) => sortOrder[address.addressType]);

  return (
    <Modal {...{ visible, onDismiss, title: "Select Preferred Address" }}>
      <View className="mb-4">
        {homeAddress && (
          <TouchableAddress address={homeAddress} onPress={onPress} isPreferredAddress={isPreferredAddress(homeAddress)}>
            <Text className="text-regular-tight font-500 text-ink-darkest mb-2">Home Address</Text>
          </TouchableAddress>
        )}

        {employments.map(({ facility }) => {
          return sortAddresses(facility.addresses).map((address, index) => (
            <TouchableAddress address={address} onPress={onPress} key={index} isPreferredAddress={isPreferredAddress(address)}>
              <Text className="text-regular-tight font-500 text-ink-darkest">{facility.name}</Text>
              <Text className="text-regular-tight font-500 text-ink-darker mt-2">{capitalize(address.addressType)}:</Text>
            </TouchableAddress>
          ));
        })}
      </View>
    </Modal>
  );
};

function mayEditEmployment(person: PersonWithProgram) {
  // terminated programs cannot edit employment
  if (person.latestProgram?.status === Status.TERMINATED) {
    return false;
  }
  switch (person.latestProgram?.programType?.name) {
    case ProgramType.Member:
    case ProgramType.Associate:
      return true;
    default:
      return false;
  }
}

export default function AccountEditScreen({ navigation }: ScreenProps<"AccountEdit">) {
  const [showEmailChangeNotice, setShowEmailChangeNotice] = useState(false);
  const { data, loading, refetch } = useQuery<Query>(ACCOUNT_EDIT_QUERY);
  const isFocused = useIsFocused();

  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [])
  );

  const hasPendingChangeRequests = data?.currentPerson?.pendingChangeRequests?.length ?? 0 > 0;
  const primarySection = data?.currentPerson?.primarySection;
  const [sendMutation] = useMutation(MUTATION);
  const [changeMemberStatusMutation, { loading: statusLoading }] = useMutation(CHANGE_STATUS_MUTATION);
  const [cancelChangeRequest, { loading: cancelChangeRequestLoading }] = useMutation(CANCEL_PENDING_CHANGE_REQUEST);
  const showSnackBar = useSnackBar();
  const [editContactPreferencesModalVisible, setEditContactPreferencesModalVisible] = useState(false);
  const [pendingChangeRequestsModalVisible, setPendingChangeRequestsModalVisible] = useState(false);
  const [sentForReviewModalVisible, setSentForReviewModalVisible] = useState(false);
  const [showStatusChangeWarningModal, setShowStatusChangeWarningModal] = useState<ChangeStatusEnum | "">("");
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false);
  const [confirmCancelPendingChangesModal, setConfirmCancelPendingChangesModal] = useState<{
    open: boolean;
    id: string | null;
  }>({
    open: false,
    id: null,
  });
  function onDismissSetEditContactPreferencesModalVisible() {
    setEditContactPreferencesModalVisible(false);
  }

  function showEditContactPreferencesModal() {
    setEditContactPreferencesModalVisible(true);
  }

  const handleStatusChangeOnContinueFn = async (status: ChangeStatusEnum | "") => {
    try {
      const { data } = await changeMemberStatusMutation({
        variables: {
          newStatus: status,
        },
      });
      if (data.changeMemberStatusRequest.success) {
        setSentForReviewModalVisible(true);
        return;
      } else {
        throw new Error(data.changeMemberStatusRequest.message);
      }
    } catch (error) {
      showSnackBar({ message: "Something went wrong", icon: "x-circle" });
    }
  };

  const handleCancelChangeRequest = async (id: string) => {
    const { data } = await cancelChangeRequest({ variables: { id } });
    if (data?.cancelChangeRequest.success) {
      showSnackBar({ message: "Change request cancelled", duration: 3000, icon: "check-circle" });
      refetch();
      setConfirmCancelPendingChangesModal({ open: false, id: null });
    } else {
      const errorMsg = data?.cancelChangeRequest.message || "Unexpected error, please try again.";
      showSnackBar({ message: errorMsg, duration: 3000, icon: "x-circle" });
    }
  };

  const intercom = useIntercom();

  if (loading || !data || statusLoading) {
    return <ActivityIndicator className="flex-1 items-center justify-center bg-white" />;
  }

  const { addresses, phoneNumbers, contactAddress, employments } = data.currentPerson;
  const homeAddress = addresses.find((a) => a.addressType === "home");
  const homePhone = phoneNumbers.find(({ phoneType }) => phoneType === "home");
  const mobilePhone = phoneNumbers.find(({ phoneType }) => phoneType === "mobile");
  const { primaryEmail, displayName, informalName } = data.currentMember;

  const navigateToAccountShow = () => navigation.navigate("AccountShow");
  const navigateToEmailChange = () => navigation.navigate("EmailChange");
  const navigateToAccountEditContact = () => navigation.getParent()?.navigate("AccountEditContact");
  const navigateToCurrentEmploymentReview = () => navigation.getParent()?.navigate("CurrentEmploymentReview");
  const navigateToAddEmployment = () => navigation.getParent()?.navigate("AddEmployment", { employmentsToRemove: [] });

  return (
    <ScreenContainer testID="AccountEditScreen" navigationTitle={navigationTitle} goBack={navigateToAccountShow}>
      <ContactChangedNotice onChangePreferences={showEditContactPreferencesModal} />
      <EmailChangeNotice
        visible={showEmailChangeNotice}
        onContinue={navigateToEmailChange}
        onDismiss={() => setShowEmailChangeNotice(false)}
      />
      <EditContactPreferencesModal
        {...{
          visible: editContactPreferencesModalVisible,
          onDismiss: onDismissSetEditContactPreferencesModalVisible,
          addresses,
          employments,
          contactAddress,
          onPress: async (address: Address) => {
            const {
              data: {
                updatePersonContactAddress: { success, message },
              },
            } = await sendMutation({
              variables: {
                id: address.id,
              },
            });

            if (success) {
              showSnackBar({ message: "Changes Saved", duration: 3000, icon: "check-circle" });
              onDismissSetEditContactPreferencesModalVisible();
            } else {
              const errorMsg = message || "Unexpected error, please try again.";
              showSnackBar({ message: errorMsg, duration: 3000, icon: "x-circle" });
              onDismissSetEditContactPreferencesModalVisible();
            }
          },
        }}
      />
      <PendingChangeRequestsModal
        {...{
          visible: pendingChangeRequestsModalVisible,
          onDismiss: () => setPendingChangeRequestsModalVisible(false),
          pendingChangeRequests: data.currentPerson.pendingChangeRequests || [],
          onContinue: (id: string) => {
            setPendingChangeRequestsModalVisible(false);
            setConfirmCancelPendingChangesModal({ open: true, id });
          },
        }}
      />
      <ChangeStatusModal
        visible={showStatusChangeModal}
        onDismiss={() => setShowStatusChangeModal(false)}
        navigation={navigation}
        setShowStatusChangeWarningModal={setShowStatusChangeWarningModal}
      />
      <InactiveStatusModals
        showStatusChangeWarningModal={showStatusChangeWarningModal}
        setShowStatusChangeWarningModal={setShowStatusChangeWarningModal}
        onContinue={() => handleStatusChangeOnContinueFn(showStatusChangeWarningModal)}
      />
      <SentForReviewScreen
        visible={sentForReviewModalVisible}
        onClose={() => {
          setSentForReviewModalVisible(false);
        }}
        messageText="Your request has been sent to Membership Services for review. Your information will not be updated until approved. Submissions are typically reviewed within 3-7 business days."
      />
      <ConfirmCancelChangeRequestModal
        mutationLoading={cancelChangeRequestLoading}
        showUnsavedChangesModal={confirmCancelPendingChangesModal.open}
        setShowUnsavedChangesModal={() => setConfirmCancelPendingChangesModal({ open: false, id: null })}
        discardAndExit={() => {
          setConfirmCancelPendingChangesModal({ open: false, id: null });
          setPendingChangeRequestsModalVisible(false);
        }}
        handleSubmit={() => {
          handleCancelChangeRequest(confirmCancelPendingChangesModal.id!);
        }}
        title="Are you sure you wish to cancel the pending change request?"
        message="Your employment change request will be canceled if you continue."
        buttonLabel="Continue"
        cancelButtonLabel="Go Back"
      />
      <ForceHomeAddressModal show={isFocused && !loading && !homeAddress} onSubmit={navigateToAccountEditContact} />
      <ScrollableContainer>
        <View className="m-6">
          <View className="flex-row justify-between pb-4 items-center">
            <Text className="text-regular-tight font-700 text-ink-darkest">Name</Text>
            <Pressable className="active:bg-sky-light" onPress={() => intercom.present(config.intercomContent.accountEditName)}>
              <Feather name="info" size={24} color={colors.primary.DEFAULT} />
            </Pressable>
          </View>
          <Text className="text-regular-tight font-400 text-ink-darker">{renderName(displayName, informalName)}</Text>
        </View>

        <Divider />

        <View className="m-6">
          <View className="flex-row justify-between items-center pb-4">
            <Text className="text-regular-tight font-700 text-ink-darkest">Email</Text>
            <LinkButton testID="edit-email-button" onPress={() => setShowEmailChangeNotice(true)}>
              Edit
            </LinkButton>
          </View>
          <Text className="text-regular-tight font-400 text-ink-darker">{primaryEmail}</Text>
        </View>

        <Divider />

        <View className="m-6">
          <View className="flex-row justify-between items-center pb-4">
            <Text className="text-regular-tight font-700 text-ink-darkest">Home Address</Text>
            <LinkButton testID="edit-address-button" onPress={navigateToAccountEditContact}>
              Edit
            </LinkButton>
          </View>
          {!!homeAddress ? (
            <DisplayAddress address={homeAddress} />
          ) : (
            <Text className="text-regular-tight font-400 text-ink-darker">None</Text>
          )}
        </View>

        <View className="mx-6 mb-6">
          <View className="flex-row justify-between items-center">
            <Text className="text-regular-tight font-700 pb-4 text-ink-darkest">Phone</Text>
          </View>
          <PersonPhone phone={homePhone} label="Home" />
          <View className="mt-6">
            <PersonPhone phone={mobilePhone} label="Mobile" />
          </View>
        </View>

        <Divider />

        <View className="m-6" testID="contact-preferences-view">
          <View className="flex-row justify-between items-center">
            <Text className="text-regular-tight font-700 text-ink-darkest">Contact Preferences</Text>
            <LinkButton testID="edit-contact-preference-button" onPress={showEditContactPreferencesModal}>
              Edit
            </LinkButton>
          </View>

          {contactAddress && (
            <Text className="text-small-tight font-400 text-ink-darker mt-4 mb-2">Send all correspondence to:</Text>
          )}
          <ContactPreferenceDisplay contactAddress={contactAddress} />
        </View>

        {mayEditEmployment(data.currentPerson) && (
          <>
            <Divider />
            <View className="m-6">
              <View className="flex-row justify-between items-center">
                <Text className="text-regular-tight font-700 text-ink-darkest">Employment</Text>
                <LinkButton
                  testID="add-employment-button"
                  onPress={employments.length ? navigateToCurrentEmploymentReview : navigateToAddEmployment}
                >
                  Add
                </LinkButton>
              </View>
              {hasPendingChangeRequests ? (
                <PersistentSnackbar
                  IconComp={() => <Feather name="info" color={colors.blue.DEFAULT} size={24} />}
                  text="Pending changes"
                  action={() => setPendingChangeRequestsModalVisible(true)}
                  borderColor="border-blue-light"
                  backgroundColor={colors.blue.lightest}
                />
              ) : null}
              {employments.length ? (
                [...employments]
                  .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))
                  .map((employment, key) => (
                    <EmploymentSection
                      key={key}
                      employment={employment}
                      hasOnlyOneEmployment={employments.length === 1}
                      primarySection={primarySection}
                    />
                  ))
              ) : (
                <Unemployed
                  primarySection={primarySection}
                  onChangeStatus={() => setShowStatusChangeModal(true)}
                  latestProgram={data.currentPerson.latestProgram}
                />
              )}
            </View>
          </>
        )}
      </ScrollableContainer>
    </ScreenContainer>
  );
}
